// Use empty string for relative paths - Vite proxy will handle routing to backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// CSRF token storage - stored in memory from login/register response
let csrfToken: string | null = null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  skipCsrf?: boolean; // For endpoints that don't need CSRF (like login)
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class RateLimitError extends ApiError {
  constructor(
    public retryAfter: number = 1
  ) {
    super(429, "You're making requests too quickly. Please wait a moment and try again.");
    this.name = 'RateLimitError';
  }
}

// Global rate limit event listeners
type RateLimitListener = (error: RateLimitError) => void;
const rateLimitListeners: Set<RateLimitListener> = new Set();

function onRateLimitError(listener: RateLimitListener): () => void {
  rateLimitListeners.add(listener);
  return () => rateLimitListeners.delete(listener);
}

function notifyRateLimitError(error: RateLimitError): void {
  rateLimitListeners.forEach(listener => listener(error));
}

// Global auth error event listeners (for 401 unauthorized responses)
type AuthErrorListener = (error: ApiError) => void;
const authErrorListeners: Set<AuthErrorListener> = new Set();

function onAuthError(listener: AuthErrorListener): () => void {
  authErrorListeners.add(listener);
  return () => authErrorListeners.delete(listener);
}

function notifyAuthError(error: ApiError): void {
  authErrorListeners.forEach(listener => listener(error));
}

// Global ToS required error listeners (for 403 TOS_REQUIRED responses)
type TosRequiredListener = () => void;
const tosRequiredListeners: Set<TosRequiredListener> = new Set();

function onTosRequired(listener: TosRequiredListener): () => void {
  tosRequiredListeners.add(listener);
  return () => tosRequiredListeners.delete(listener);
}

function notifyTosRequired(): void {
  tosRequiredListeners.forEach(listener => listener());
}

// CSRF token management
function setCSRFToken(token: string | null): void {
  csrfToken = token;
}

function getCSRFToken(): string | null {
  return csrfToken;
}

// Helper to read CSRF token from cookie (fallback if memory token is lost)
function getCSRFTokenFromCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'squareup_csrf') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Clear CSRF token (on logout)
function clearCSRFToken(): void {
  csrfToken = null;
}

// Check if method requires CSRF protection
function requiresCSRF(method: string): boolean {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, skipCsrf = false } = options;

  // Add CSRF token for state-changing requests
  if (requiresCSRF(method) && !skipCsrf) {
    const token = csrfToken || getCSRFTokenFromCookie();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials: 'include', // Include cookies in requests
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    // Handle rate limit errors specially
    if (response.status === 429) {
      let retryAfter = 1;
      try {
        const errorData = await response.json();
        retryAfter = errorData.retryAfter || 1;
      } catch {
        // Use header fallback
        const retryHeader = response.headers.get('Retry-After');
        if (retryHeader) {
          retryAfter = parseInt(retryHeader, 10) || 1;
        }
      }
      const error = new RateLimitError(retryAfter);
      notifyRateLimitError(error);
      throw error;
    }

    let errorMessage = 'An error occurred';
    let errorCode: string | undefined;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      errorCode = errorData.code;
    } catch {
      // Ignore JSON parse errors
    }
    const error = new ApiError(response.status, errorMessage);

    // Handle auth errors (401 Unauthorized) - notify listeners to trigger logout/redirect
    if (response.status === 401) {
      notifyAuthError(error);
    }

    // Handle ToS required errors (403 with TOS_REQUIRED code) - notify listeners to show ToS modal
    if (response.status === 403 && errorCode === 'TOS_REQUIRED') {
      notifyTosRequired();
    }

    throw error;
  }

  // Handle empty responses (204 No Content or empty body)
  if (response.status === 204) {
    return {} as T;
  }

  // Check content-length header for empty responses
  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return {} as T;
  }

  // Try to parse JSON, handle empty body gracefully
  const text = await response.text();
  if (!text || text.trim() === '') {
    return {} as T;
  }

  return JSON.parse(text);
}

// Public request function - does not include auth cookies or CSRF token
async function requestPublic<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    // Note: No credentials for public requests
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    // Handle rate limit errors specially
    if (response.status === 429) {
      let retryAfter = 1;
      try {
        const errorData = await response.json();
        retryAfter = errorData.retryAfter || 1;
      } catch {
        // Use header fallback
        const retryHeader = response.headers.get('Retry-After');
        if (retryHeader) {
          retryAfter = parseInt(retryHeader, 10) || 1;
        }
      }
      const error = new RateLimitError(retryAfter);
      notifyRateLimitError(error);
      throw error;
    }

    let errorMessage = 'An error occurred';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }
    throw new ApiError(response.status, errorMessage);
  }

  // Handle empty responses (204 No Content or empty body)
  if (response.status === 204) {
    return {} as T;
  }

  // Check content-length header for empty responses
  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return {} as T;
  }

  // Try to parse JSON, handle empty body gracefully
  const text = await response.text();
  if (!text || text.trim() === '') {
    return {} as T;
  }

  return JSON.parse(text);
}

export {
  API_BASE_URL,
  ApiError,
  RateLimitError,
  onRateLimitError,
  onAuthError,
  onTosRequired,
  request,
  requestPublic,
  setCSRFToken,
  getCSRFToken,
  clearCSRFToken
};
